/**
 * Unit Converter — shared between frontend and backend.
 *
 * All DB storage is canonical metric (km, kg, m, °C, L).
 * Conversion happens at API boundaries and display layer.
 * The tenant's unitSystem ('imperial' | 'metric') determines display format.
 *
 * @see docs/planning/turkey-localization/03-units-and-measurements.md
 */

// ─── Conversion Factors ─────────────────────────────────────────────────────

const MILES_PER_KM = 0.621371;
const KM_PER_MILE = 1.60934;

const LBS_PER_KG = 2.20462;
const KG_PER_LB = 0.453592;

const CUFT_PER_M3 = 35.3147;
const M3_PER_CUFT = 1 / CUFT_PER_M3;

const GAL_PER_LITER = 0.264172;
const LITERS_PER_GAL = 3.78541;

const MPH_PER_KMH = 0.621371;
const KMH_PER_MPH = 1.60934;

const FT_PER_M = 3.28084;
const M_PER_FT = 0.3048;

const INCHES_PER_CM = 0.393701;
const CM_PER_INCH = 2.54;

// Fuel-efficiency: US MPG ↔ metric L/100km.
// The conversion is reciprocal-symmetric: mpg = 235.215 / (L/100km) and
// L/100km = 235.215 / mpg. 235.215 = (100 km × LITERS_PER_GAL) / KM_PER_MILE
// (i.e. liters per gallon over km per mile, scaled to 100 km).
const MPG_LPER100KM_FACTOR = 235.215;

// ─── UnitConverter ──────────────────────────────────────────────────────────

export const UnitConverter = {
  // Distance (canonical: km)
  kmToMiles: (km: number): number => km * MILES_PER_KM,
  milesToKm: (miles: number): number => miles * KM_PER_MILE,

  // Weight (canonical: kg)
  kgToLbs: (kg: number): number => kg * LBS_PER_KG,
  lbsToKg: (lbs: number): number => lbs * KG_PER_LB,

  // Volume (canonical: m³)
  m3ToCuft: (m3: number): number => m3 * CUFT_PER_M3,
  cuftToM3: (ft3: number): number => ft3 * M3_PER_CUFT,

  // Temperature (canonical: °C)
  celsiusToFahrenheit: (c: number): number => c * (9 / 5) + 32,
  fahrenheitToCelsius: (f: number): number => (f - 32) * (5 / 9),

  // Fuel (canonical: liters)
  litersToGallons: (l: number): number => l * GAL_PER_LITER,
  gallonsToLiters: (gal: number): number => gal * LITERS_PER_GAL,

  // Fuel efficiency (canonical: L/100km). Reciprocal conversion — guards 0.
  lPer100kmToMpg: (v: number): number => (v > 0 ? MPG_LPER100KM_FACTOR / v : 0),
  mpgToLPer100km: (v: number): number => (v > 0 ? MPG_LPER100KM_FACTOR / v : 0),

  // Speed (canonical: km/h)
  kmhToMph: (kmh: number): number => kmh * MPH_PER_KMH,
  mphToKmh: (mph: number): number => mph * KMH_PER_MPH,

  // Length (canonical: meters)
  metersToFeet: (m: number): number => m * FT_PER_M,
  feetToMeters: (ft: number): number => ft * M_PER_FT,
  cmToInches: (cm: number): number => cm * INCHES_PER_CM,
  inchesToCm: (inches: number): number => inches * CM_PER_INCH,

  // Cost-per-distance rates (inverse of distance conversion):
  // if 1 mile = 1.609 km, then $X/mile = $X ÷ 1.609 per km.
  perMileToPerKm: (perMile: number): number => perMile * MILES_PER_KM,
  perKmToPerMile: (perKm: number): number => perKm * KM_PER_MILE,

  // Desi (Turkish volumetric weight): (L cm × W cm × H cm) / divisor
  // Road freight divisor: 3000, air freight: 5000
  calculateDesi: (
    lengthCm: number,
    widthCm: number,
    heightCm: number,
    divisor: number = 3000
  ): number => (lengthCm * widthCm * heightCm) / divisor,

  // Billable weight: max of actual weight and volumetric weight
  billableWeight: (actualKg: number, volumetricKg: number): number =>
    Math.max(actualKg, volumetricKg),
} as const;
